import React, { useState } from 'react';
import { DistrictStats } from '../types';

interface CantonMapProps {
  districtStats: DistrictStats[];
  onDistrictClick: (districtId: number) => void;
  onDistrictHover?: (district: DistrictStats | null) => void;
}

const CantonMap: React.FC<CantonMapProps> = ({ districtStats, onDistrictClick, onDistrictHover }) => {
  const [hoveredDistrict, setHoveredDistrict] = useState<DistrictStats | null>(null);

  // Simple, clean grid layout that makes visual sense
  const districtLayout = [
    { id: 1, name: 'Adligenswil', row: 1, col: 1 },
    { id: 2, name: 'Luzern', row: 2, col: 2 },
    { id: 3, name: 'Horw', row: 3, col: 1 },
    { id: 4, name: 'Meggen', row: 1, col: 3 },
    { id: 5, name: 'Ebikon', row: 2, col: 3 },
    { id: 6, name: 'Malters', row: 3, col: 3 },
    { id: 7, name: 'Kriens', row: 2, col: 1 },
    { id: 8, name: 'Emmen', row: 4, col: 2 },
    { id: 0, name: 'Alle Gemeinden', row: 4, col: 1, isAllDistricts: true },
  ];

  const getTemperatureColor = (temp: number) => {
    if (temp < 6) return '#284496';      // Sehr kalt
    if (temp < 11) return '#5d6ab0';     // Kalt
    if (temp < 15) return '#9498cc';     // Kühl
    if (temp < 19) return '#cacbe7';     // Leicht kühl
    if (temp < 22) return '#ffffff';     // Behaglich
    if (temp < 26) return '#f0cdbe';     // Leicht warm
    if (temp < 32) return '#e09a80';     // Warm
    if (temp < 37) return '#cf674a';     // Heiss
    return '#be331e';                    // Sehr heiss
  };

  const handleDistrictHover = (district: DistrictStats | null) => {
    setHoveredDistrict(district);
    if (onDistrictHover) {
      onDistrictHover(district);
    }
  };

  return (
    <div className="w-full">

      {/* Main content area with proper spacing */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Districts grid */}
        <div className="flex-1 relative">
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {districtLayout.map((district) => {
              let stats: DistrictStats | undefined, avgTemp: number, color: string, hasData: boolean, isHovered: boolean;
              
              if (district.isAllDistricts) {
                // Use the "Alle Gemeinden" stats directly from the districtStats array
                const alleGemeindenStats = districtStats.find(d => d.districtId === 0);
                
                if (alleGemeindenStats) {
                  // Use the pre-calculated "Alle Gemeinden" stats
                  stats = alleGemeindenStats;
                  avgTemp = alleGemeindenStats.avgMeasuredTemp;
                  color = getTemperatureColor(avgTemp);
                  hasData = true;
                  isHovered = hoveredDistrict?.districtId === 0;
                } else {
                  // Fallback to calculation if "Alle Gemeinden" stats not found
                  const allMeasurements = districtStats.reduce((total, d) => total + d.totalMeasurements, 0);
                  const allAvgTemp = districtStats.length > 0 
                    ? districtStats.reduce((sum, d) => sum + d.avgMeasuredTemp, 0) / districtStats.length 
                    : 20;
                  const allMaxTemp = districtStats.length > 0 
                    ? Math.max(...districtStats.map(d => d.maxTemp)) 
                    : 20;
                  
                  stats = {
                    districtId: 0,
                    districtName: 'Alle Gemeinden',
                    totalMeasurements: allMeasurements,
                    avgMeasuredTemp: allAvgTemp,
                    avgPerceivedTemp: allAvgTemp,
                    maxTemp: allMaxTemp,
                    minTemp: districtStats.length > 0 ? Math.min(...districtStats.map(d => d.minTemp)) : 0,
                    locations: districtStats.flatMap(d => d.locations)
                  };
                  avgTemp = allAvgTemp;
                  color = getTemperatureColor(avgTemp);
                  hasData = districtStats.length > 0;
                  isHovered = hoveredDistrict?.districtId === 0;
                }
              } else {
                stats = districtStats.find(d => d.districtId === district.id);
                avgTemp = stats?.avgMeasuredTemp || 20;
                color = getTemperatureColor(avgTemp);
                hasData = stats !== undefined;
                isHovered = hoveredDistrict?.districtId === district.id;
              }

              return (
                <div
                  key={district.id}
                  className={`relative cursor-pointer transition-all duration-300 rounded-lg border-2 shadow-md ${
                    isHovered 
                      ? 'border-white shadow-xl scale-105 z-20' 
                      : hasData 
                        ? 'border-gray-300 hover:border-primary hover:shadow-lg' 
                        : 'border-gray-200 opacity-50'
                  }`}
                  style={{
                    backgroundColor: color,
                    minHeight: '80px',
                  }}
                  onMouseEnter={() => handleDistrictHover(stats || null)}
                  onMouseLeave={() => handleDistrictHover(null)}
                  onClick={() => onDistrictClick(district.id)}
                >
                  <div className="absolute inset-0 flex items-center justify-center p-2">
                    <span className={`text-sm font-semibold text-center drop-shadow-sm ${
                      color === '#ffffff' || color === '#cacbe7' || color === '#f0cdbe' ? 'text-gray-800' : 'text-white'
                    }`}>
                      {district.name}
                    </span>
                  </div>
                  
                  {/* Data indicator */}
                  <div className={`absolute top-2 right-2 w-3 h-3 rounded-full shadow-sm ${
                    hasData ? 'bg-primary' : 'bg-accent'
                  }`}></div>


                </div>
              );
            })}
          </div>

          {/* Data Availability Legend - positioned under the grid */}
          <div className="mt-4 flex justify-start max-w-2xl mx-auto">
            <div className="inline-flex items-center space-x-6 bg-white rounded-lg p-3 shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-sm text-gray-700">Daten verfügbar</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-accent rounded-full"></div>
                <span className="text-sm text-gray-700">Keine Daten</span>
              </div>
            </div>
          </div>

          {/* Hover Tooltip - Only show if no external hover handler */}
          {hoveredDistrict && !onDistrictHover && (
            <div className="absolute bg-white rounded-lg shadow-xl p-4 border border-gray-200 z-30 max-w-xs mt-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {hoveredDistrict.districtName}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Standorte:</span>
                  <span className="font-medium">{hoveredDistrict.locations.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Messungen:</span>
                  <span className="font-medium">{hoveredDistrict.totalMeasurements}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ø Temperatur:</span>
                  <span className="font-medium">{hoveredDistrict.avgMeasuredTemp.toFixed(1)}°C</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max. Temperatur:</span>
                  <span className="font-medium">{hoveredDistrict.maxTemp.toFixed(1)}°C</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar with legend and navigation */}
        <div className="lg:w-64 space-y-4">
          {/* Temperature Legend */}
          <div className="bg-white rounded-lg p-4 shadow-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Temperatur-Skala</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#284496' }}></div>
                <span>Sehr kalt (0-6°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#5d6ab0' }}></div>
                <span>Kalt (6-11°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9498cc' }}></div>
                <span>Kühl (11-15°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#cacbe7' }}></div>
                <span>Leicht kühl (15-19°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: '#ffffff' }}></div>
                <span>Behaglich (19-22°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f0cdbe' }}></div>
                <span>Leicht warm (22-26°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#e09a80' }}></div>
                <span>Warm (26-32°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#cf674a' }}></div>
                <span>Heiss (32-37°C)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#be331e' }}></div>
                <span>Sehr heiss (&gt;37°C)</span>
              </div>
            </div>
          </div>

          {/* Navigation Instructions */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Navigation</h3>
            <p className="text-xs text-gray-600">
              Hover über eine Gemeinde für Details. Klick für das Dashboard.
            </p>
          </div>
        </div>
      </div>



      {/* Mobile instructions */}
      <div className="mt-4 text-center lg:hidden">
        <p className="text-sm text-gray-600">
          Tippen Sie auf eine Gemeinde für das Dashboard
        </p>
      </div>
    </div>
  );
};

export default CantonMap;
